import React, { useState, useEffect, useCallback } from 'react';
import {
  Sparkles, MessageSquare, RefreshCw, AlertCircle, CheckCircle,
  Clock, Users, Cpu, Bot, Activity, Database, Settings, Zap,
  TrendingUp, AlertTriangle, ChevronDown, ChevronUp, Play,
  Eye, EyeOff, Info, Server
} from 'lucide-react';
import { toast } from '../../utils/toastUtils';
import { useI18n } from '../../i18n';

const API_BASE = process.env.REACT_APP_API_URL || '';

const AIAssistantSettings = () => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [actions, setActions] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [expandedSection, setExpandedSection] = useState('status');
  const [claudeCodeStatus, setClaudeCodeStatus] = useState(null);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
  });

  // Fetch AI Assistant status
  const fetchStatus = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai-assistant/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
      }
    } catch (error) {
      console.error('Error fetching AI Assistant status:', error);
    }
  }, []);

  // Fetch AI Assistant actions
  const fetchActions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai-assistant/actions`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActions(data.actions || []);
      }
    } catch (error) {
      console.error('Error fetching AI Assistant actions:', error);
    }
  }, []);

  // Fetch conversations stats
  const fetchConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/v1/ai-assistant/conversations?page_size=5`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations || []);
        setStats({
          total_conversations: data.total || 0,
          page: data.page || 1
        });
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, []);

  // Check Claude Code API status
  const checkClaudeCodeStatus = useCallback(async () => {
    try {
      // Try to get Claude Code status via backend
      const response = await fetch(`${API_BASE}/api/v1/ai-assistant/claude-code/status`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setClaudeCodeStatus(data);
      } else {
        setClaudeCodeStatus({ available: false, error: 'Status endpoint not available' });
      }
    } catch (error) {
      setClaudeCodeStatus({ available: false, error: error.message });
    }
  }, []);

  // Load all data
  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([
      fetchStatus(),
      fetchActions(),
      fetchConversations(),
      checkClaudeCodeStatus()
    ]);
    setLoading(false);
  }, [fetchStatus, fetchActions, fetchConversations, checkClaudeCodeStatus]);

  useEffect(() => {
    loadAll();
    // Refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStatus();
      checkClaudeCodeStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, [loadAll, fetchStatus, checkClaudeCodeStatus]);

  const toggleSection = (section) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-brand-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-brand-green mb-2 flex items-center gap-3">
            <Sparkles className="w-7 h-7" />
            Assistant IA - Configuration
          </h2>
          <p className="text-gray-400">
            Configuration et monitoring de l'Assistant IA avec prise en charge automatique par l'Agent IA
          </p>
        </div>
        <button
          onClick={loadAll}
          className="flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('common.refresh')}
        </button>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Compute Pool Status - Dynamic GPU/CPU */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${status?.gpu_pool?.available ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
              <Cpu className={`w-5 h-5 ${status?.gpu_pool?.available ? 'text-green-400' : 'text-red-400'}`} />
            </div>
            <span className="text-gray-300 font-medium">
              {status?.gpu_pool?.gpu_memory_total > 0 ? 'GPU Pool' : 'CPU Inference'}
            </span>
          </div>
          <div className="space-y-1">
            <div className={`text-lg font-bold ${status?.gpu_pool?.available ? 'text-green-400' : 'text-red-400'}`}>
              {status?.gpu_pool?.available ? 'Disponible' : 'Indisponible'}
            </div>
            {status?.gpu_pool?.models_loaded?.length > 0 && (
              <div className="text-sm text-gray-400">
                Model: {status.gpu_pool.models_loaded[0]}
              </div>
            )}
            <div className="text-xs text-gray-500">
              {status?.gpu_pool?.gpu_memory_total > 0 ? 'Mode GPU' : 'Mode CPU'}
            </div>
          </div>
        </div>

        {/* Claude Code Status */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center gap-3 mb-3">
            <div className={`p-2 rounded-lg ${claudeCodeStatus?.available ? 'bg-green-500/20' : 'bg-yellow-500/20'}`}>
              <Bot className={`w-5 h-5 ${claudeCodeStatus?.available ? 'text-green-400' : 'text-yellow-400'}`} />
            </div>
            <span className="text-gray-300 font-medium">Claude Code API</span>
          </div>
          <div className="space-y-1">
            <div className={`text-lg font-bold ${claudeCodeStatus?.available ? 'text-green-400' : 'text-yellow-400'}`}>
              {claudeCodeStatus?.available ? 'Connected' : 'Not available'}
            </div>
            <div className="text-sm text-gray-400">
              {claudeCodeStatus?.available ? 'Auto-corrections enabled' : 'Degraded mode'}
            </div>
          </div>
        </div>

        {/* Conversations Today */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/20">
              <MessageSquare className="w-5 h-5 text-blue-400" />
            </div>
            <span className="text-gray-300 font-medium">Conversations</span>
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold text-white">
              {stats?.total_conversations || 0}
            </div>
            <div className="text-sm text-gray-400">Total conversations</div>
          </div>
        </div>

        {/* Agent Takeover */}
        <div className="bg-brand-black-light rounded-xl p-4 border border-brand-green/30">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <Activity className="w-5 h-5 text-purple-400" />
            </div>
            <span className="text-gray-300 font-medium">Agent IA</span>
          </div>
          <div className="space-y-1">
            <div className="text-lg font-bold text-green-400">Actif</div>
            <div className="text-sm text-gray-400">Prise en charge automatique</div>
          </div>
        </div>
      </div>

      {/* Configuration Section */}
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
        <button
          onClick={() => toggleSection('config')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-green/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-brand-green" />
            <span className="text-lg font-semibold text-white">Configuration Agent/Assistant</span>
          </div>
          {expandedSection === 'config' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'config' && (
          <div className="p-4 border-t border-brand-green/20 space-y-4">
            {/* Auto-fix Configuration */}
            <div className="bg-brand-black/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                Correction Automatique par Agent IA
              </h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-gray-300">AttributeError detection</span>
                  </div>
                  <span className="text-green-400 text-sm">Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-gray-300">Correction via Claude Code</span>
                  </div>
                  <span className="text-green-400 text-sm">Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-gray-300">Automatic backend restart</span>
                  </div>
                  <span className="text-green-400 text-sm">Enabled</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    <span className="text-gray-300">Handoff vers Ollama</span>
                  </div>
                  <span className="text-green-400 text-sm">Enabled</span>
                </div>
              </div>
            </div>

            {/* Error Types */}
            <div className="bg-brand-black/50 rounded-lg p-4">
              <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400" />
                Automatically handled error types
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  AttributeError (code fixed)
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  ImportError (code fixed)
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  TypeError (code fixed)
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  KeyError (code fixed)
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                  ConnectionError (monitored)
                </div>
                <div className="flex items-center gap-2 text-gray-300">
                  <div className="w-2 h-2 rounded-full bg-yellow-400"></div>
                  Ollama Error (monitored)
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions Section */}
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
        <button
          onClick={() => toggleSection('actions')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-green/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Play className="w-5 h-5 text-brand-green" />
            <span className="text-lg font-semibold text-white">Suggested Actions ({actions.length})</span>
          </div>
          {expandedSection === 'actions' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'actions' && (
          <div className="p-4 border-t border-brand-green/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {actions.map((action) => (
                <div
                  key={action.action_key}
                  className="bg-brand-black/50 rounded-lg p-3 flex items-center justify-between"
                >
                  <div>
                    <div className="text-white font-medium">{action.label}</div>
                    <div className="text-sm text-gray-400">{action.description}</div>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs ${
                    action.action_type === 'guided' ? 'bg-purple-500/20 text-purple-300' :
                    action.action_type === 'navigate' ? 'bg-blue-500/20 text-blue-300' :
                    'bg-gray-500/20 text-gray-300'
                  }`}>
                    {action.action_type}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Recent Conversations */}
      <div className="bg-brand-black-light rounded-xl border border-brand-green/30 overflow-hidden">
        <button
          onClick={() => toggleSection('conversations')}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-brand-green/5 transition-colors"
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="w-5 h-5 text-brand-green" />
            <span className="text-lg font-semibold text-white">Recent Conversations</span>
          </div>
          {expandedSection === 'conversations' ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </button>

        {expandedSection === 'conversations' && (
          <div className="p-4 border-t border-brand-green/20">
            {conversations.length > 0 ? (
              <div className="space-y-3">
                {conversations.map((conv) => (
                  <div
                    key={conv.conversation_id}
                    className="bg-brand-black/50 rounded-lg p-3"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-white font-medium">{conv.title || 'Conversation'}</div>
                      <div className="text-xs text-gray-400">
                        {new Date(conv.created_at).toLocaleString('fr-FR')}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span>{conv.message_count || 0} messages</span>
                      <span className={`px-2 py-0.5 rounded ${
                        conv.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-gray-500/20 text-gray-300'
                      }`}>
                        {conv.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-400 py-8">
                No recent conversations
              </div>
            )}
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5" />
          <div>
            <h4 className="text-blue-300 font-medium mb-1">Fonctionnement Agent/Assistant</h4>
            <p className="text-sm text-gray-400">
              The AI Assistant uses Ollama (CPU/GPU) to respond to users.
              En cas d'erreur, l'Agent IA prend automatiquement le relais pour diagnostiquer
              and fix the issue. Once fixed, it hands back control
              to the standard AI Assistant.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantSettings;
